import { CustomerPortalBtn } from 'components/CustomerPortalBtn';
import EmbedClaimPortal from '../EmbedClaimPortal/EmbedClaimPortal';
import SettingsViewWrapper from 'components/SettingsWrapperView';
import { withNotification } from 'components/Notification';
import { NotificationTypes } from 'utils';

const Portal = withNotification(
  ({ showNotification }: { showNotification: any }) => {
    const portalUrl = 'https://resolve.shipaid.com';

    const handleCopyUrl = () => {
      navigator.clipboard.writeText(portalUrl);
      showNotification(
        NotificationTypes.success,
        'Portal URL copied to clipboard',
      );
    };

    return (
      <SettingsViewWrapper
        sectionId="resolution-portal"
        activeNav="resolution-portal/portal"
      >
        <div className="w-full h-full bg-[#f6f6f7]">
          <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-[28px] font-semibold text-[#202223] leading-tight mb-2">
                Portal Settings
              </h1>
              <p className="text-[#6d7175] text-sm">
                Choose how customers access your claims portal. Use the
                Universal Portal for a quick, ready-to-use solution, or
                customize the Embedded Portal to match your brand.
              </p>
            </div>

            {/* Universal Claims Portal Access */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-base font-semibold text-[#202223]">
                    Universal Claims Portal
                  </h2>
                  <span className="px-2 py-0.5 text-xs font-medium text-[#6d7175] bg-[#f6f6f7] rounded border border-[#e1e3e5]">
                    Ready to Use
                  </span>
                </div>
                <p className="text-[#6d7175] text-sm leading-relaxed max-w-3xl mb-3">
                  Use our hosted portal solution. Simply point your customers to{' '}
                  <code className="px-1.5 py-0.5 bg-[#f6f6f7] border border-[#e1e3e5] rounded text-[#202223] font-mono text-xs">
                    resolve.shipaid.com
                  </code>{' '}
                  via links on your website, in emails, chat messages, or
                  anywhere else.
                  <strong className="text-[#202223]">
                    {' '}
                    No customization available
                  </strong>{' '}
                  — this is a standard, ready-to-use portal.
                </p>

                {/* URL Copy Section */}
                <div className="bg-[#f6f6f7] rounded-lg p-3 border border-[#e1e3e5] mb-4">
                  <p className="text-xs text-[#6d7175] font-medium mb-2">
                    Portal URL:
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="flex-1 min-w-[200px] px-3 py-2 bg-white border border-[#e1e3e5] rounded text-[#202223] font-mono text-sm">
                      {portalUrl}
                    </code>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCopyUrl}
                        className="px-4 py-2 bg-[#2563EB] text-white text-sm font-medium rounded-lg hover:bg-[#1d4ed8] transition-colors whitespace-nowrap"
                      >
                        Copy URL
                      </button>
                      <CustomerPortalBtn />
                    </div>
                  </div>
                </div>

                {/* Best for and How to use side by side */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-[#f6f6f7] rounded-lg p-3 border border-[#e1e3e5]">
                    <p className="text-xs text-[#6d7175] font-medium mb-1">
                      Best for:
                    </p>
                    <ul className="text-xs text-[#6d7175] space-y-1 list-disc list-inside">
                      <li>Quick setup with no configuration needed</li>
                      <li>Standard branding and design</li>
                      <li>Direct link sharing with customers</li>
                    </ul>
                  </div>
                  <div className="bg-[#f6f6f7] rounded-lg p-3 border border-[#e1e3e5]">
                    <p className="text-xs text-[#6d7175] font-medium mb-2">
                      How to use:
                    </p>
                    <p className="text-xs text-[#6d7175] mb-2">
                      Add a link to{' '}
                      <code className="px-1 py-0.5 bg-white border border-[#e1e3e5] rounded font-mono">
                        resolve.shipaid.com
                      </code>{' '}
                      in:
                    </p>
                    <ul className="text-xs text-[#6d7175] space-y-1 list-disc list-inside">
                      <li>Your store website or footer</li>
                      <li>Customer service emails and correspondence</li>
                      <li>Live chat or support messages</li>
                      <li>Order confirmation emails</li>
                      <li>Anywhere you communicate with customers</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Embedded Claims Portal (Customized) */}
            <section className="bg-white rounded-lg border border-[#e1e3e5] p-6">
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-base font-semibold text-[#202223]">
                    Embedded Claims Portal
                  </h2>
                  <span className="px-2 py-0.5 text-xs font-medium text-[#2563EB] bg-[#eff6ff] rounded border border-[#2563EB]">
                    Fully Customizable
                  </span>
                </div>
                <p className="text-[#6d7175] text-sm leading-relaxed max-w-3xl mb-3">
                  Embed a fully customizable portal directly into your website
                  or Shopify store.
                  <strong className="text-[#202223]">
                    {' '}
                    Complete control over design, branding, colors, and labels
                  </strong>{' '}
                  to match your brand identity.
                </p>
                <div className="bg-[#eff6ff] rounded-lg p-3 border border-[#2563EB] mb-4">
                  <p className="text-xs text-[#2563EB] font-medium mb-1">
                    Best for:
                  </p>
                  <ul className="text-xs text-[#6d7175] space-y-1 list-disc list-inside">
                    <li>Full brand customization and white-labeling</li>
                    <li>Integration into your existing website</li>
                    <li>Custom templates, colors, logos, and translations</li>
                  </ul>
                </div>
              </div>
              <EmbedClaimPortal />
            </section>
          </div>
        </div>
      </SettingsViewWrapper>
    );
  },
);

export default Portal;
